import { FaqSchema } from "@/components/FaqSchema";
import { ContentPageNav } from "@/components/ContentPageNav";
import { Footer } from "@/components/Footer";

const html = `<nav aria-label="Kruimelpad" class="px-3 pt-4 pb-2"><div class="container mx-auto max-w-6xl"><ol class="flex flex-wrap items-center gap-2 text-sm"><li class="flex items-center gap-2"><a href="/" class="text-muted-foreground hover:text-foreground transition-colors">Home</a></li><li class="text-foreground font-medium" aria-current="page">Wat is husselen?</li></ol></div></nav>

  <header class="py-10 md:py-20 px-3">
    <div class="container mx-auto max-w-3xl">
      <h1 class="text-3xl md:text-5xl font-bold text-foreground mb-6">Wat is husselen bij tennis?</h1>
      <p class="text-lg md:text-xl text-muted-foreground leading-relaxed"><strong>Husselen</strong> is een populaire speelvorm bij tennisverenigingen waarbij spelers per ronde wisselend aan elkaar gekoppeld worden voor dubbelspel. Geen vaste teams, geen vaste tegenstanders — iedere ronde nieuwe partners en wedstrijden.</p>
    </div>
  </header>

  <article class="px-3 pb-6">
    <div class="container mx-auto max-w-3xl space-y-8">
      
        <section>
          <h2 class="text-2xl md:text-3xl font-bold text-foreground mb-4">Definitie: husselen in het kort</h2>
          <div class="text-muted-foreground leading-relaxed space-y-4"><p>Husselen — soms ook geschreven als "hussel" — komt van het Engelse <em>to hustle</em> / <em>to shuffle</em> en betekent "door elkaar mengen". Bij een husselavond melden leden zich aan, worden ze door een husselaar of een algoritme over de banen verdeeld, en wisselen partners en tegenstanders elke 30 minuten.</p><p>Het doel: zoveel mogelijk verschillende leden tegen elkaar laten spelen, op een gezellige en sportieve manier.</p></div>
        </section>
        <section>
          <h2 class="text-2xl md:text-3xl font-bold text-foreground mb-4">Hoe werkt een husselavond op de club?</h2>
          <div class="text-muted-foreground leading-relaxed space-y-4"><p>Een typische husselavond verloopt zo:</p><ol class="list-decimal pl-6 space-y-1"><li>De club reserveert een aantal banen voor het hussel-moment.</li><li>Leden melden zich aan (via een lijst, app of aan de bar).</li><li>De husselaar maakt per ronde een indeling — vaak op niveau gemixt.</li><li>Spelers spelen 30 minuten en komen daarna terug voor een nieuwe indeling.</li><li>Dit herhaalt zich tot het einde van de avond.</li></ol></div>
        </section>
        <section>
          <h2 class="text-2xl md:text-3xl font-bold text-foreground mb-4">Waarom is husselen zo populair?</h2>
          <div class="text-muted-foreground leading-relaxed space-y-4"><ul class="list-disc pl-6 space-y-1"><li>Je leert nieuwe leden van je vereniging kennen.</li><li>Je hoeft geen team te regelen — gewoon aanmelden en spelen.</li><li>Wisselende partners maken het sportief uitdagend.</li><li>Het is gezellig en laagdrempelig.</li></ul></div>
        </section>
        <section>
          <h2 class="text-2xl md:text-3xl font-bold text-foreground mb-4">Husselen, tossen en racket trekken — hetzelfde?</h2>
          <div class="text-muted-foreground leading-relaxed space-y-4"><p>In de praktijk worden de termen door elkaar gebruikt. <a href="/husselen-vs-tossen-vs-racket-trekken" class="text-accent hover:underline">In dit overzicht vergelijken we de drie speelvormen</a> zodat je weet welke term op jouw club het beste past.</p></div>
        </section>
        <section>
          <h2 class="text-2xl md:text-3xl font-bold text-foreground mb-4">Husselavond automatiseren</h2>
          <div class="text-muted-foreground leading-relaxed space-y-4"><p>De meeste husselavonden worden nog handmatig georganiseerd door vrijwilligers. Dat kost tijd, vraagt om een vaste husselaar en zorgt voor wachttijd bij elke nieuwe ronde. De Hussel App automatiseert dit volledig: aanmelden, indelen op niveau, push-meldingen per ronde — alles via de telefoon van je leden. <a href="/husselavond-organiseren" class="text-accent hover:underline">Lees hoe je een husselavond organiseert met De Hussel App</a>.</p></div>
        </section>
    </div>
  </article>

  <section class="py-10 md:py-16 px-3">
    <div class="container mx-auto max-w-3xl">
      <div class="p-5 md:p-10 text-center rounded-lg border border-accent/30 bg-accent/10">
        <h2 class="text-2xl md:text-3xl font-bold text-foreground mb-4">Klaar om dit voor je club te automatiseren?</h2>
        <p class="text-lg text-muted-foreground mb-6">De Hussel App regelt aanmelden, indelen en push-meldingen — voor husselen, tossen en racket trekken.</p>
        <a href="/club-register" class="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-accent text-accent-foreground font-medium hover:bg-accent/90 transition-colors">Registreer je club </a>
      </div>
    </div>
  </section>

  
  <section class="py-10 md:py-16 px-3">
    <div class="container mx-auto max-w-4xl">
      <h2 class="text-2xl md:text-3xl font-bold text-foreground mb-6 md:mb-8 text-center">Veelgestelde vragen</h2>
      <div class="space-y-3">
    <details class="group rounded-lg border border-border bg-card p-4 md:p-5">
      <summary class="flex items-center justify-between gap-3 cursor-pointer list-none">
        <h3 class="text-base md:text-lg font-semibold text-foreground">Wat betekent husselen in het tennis?</h3>
        
      </summary>
      <div class="mt-3 text-muted-foreground leading-relaxed">Husselen betekent dat spelers per ronde door elkaar gemixt worden — wisselende partners en wisselende tegenstanders, meestal in dubbelspel. Het is een speelvorm voor vrij spelen op de club, niet voor competitie.</div>
    </details>
    <details class="group rounded-lg border border-border bg-card p-4 md:p-5">
      <summary class="flex items-center justify-between gap-3 cursor-pointer list-none">
        <h3 class="text-base md:text-lg font-semibold text-foreground">Hoe lang duurt een husselronde?</h3>
        
      </summary>
      <div class="mt-3 text-muted-foreground leading-relaxed">Een husselronde duurt meestal 30 minuten, soms 25 of 45 afhankelijk van de club. Na elke ronde wordt opnieuw ingedeeld.</div>
    </details>
    <details class="group rounded-lg border border-border bg-card p-4 md:p-5">
      <summary class="flex items-center justify-between gap-3 cursor-pointer list-none">
        <h3 class="text-base md:text-lg font-semibold text-foreground">Moet ik een vast niveau hebben om mee te husselen?</h3>
        
      </summary>
      <div class="mt-3 text-muted-foreground leading-relaxed">Nee. Husselavonden zijn juist bedoeld om gemengd op niveau te spelen. Een goede indeling — handmatig of via een algoritme — zorgt dat partijen gelijkwaardig blijven.</div>
    </details>
    <details class="group rounded-lg border border-border bg-card p-4 md:p-5">
      <summary class="flex items-center justify-between gap-3 cursor-pointer list-none">
        <h3 class="text-base md:text-lg font-semibold text-foreground">Wat is het verschil tussen husselen en competitie?</h3>
        
      </summary>
      <div class="mt-3 text-muted-foreground leading-relaxed">Husselen is recreatief en vrijblijvend: aanmelden wanneer je wilt, geen vaste teams. Competitie kent vaste teams, een speelschema en een ranglijst.</div>
    </details>
    <details class="group rounded-lg border border-border bg-card p-4 md:p-5">
      <summary class="flex items-center justify-between gap-3 cursor-pointer list-none">
        <h3 class="text-base md:text-lg font-semibold text-foreground">Hoeveel spelers heb je nodig voor een husselavond?</h3>
        
      </summary>
      <div class="mt-3 text-muted-foreground leading-relaxed">In principe minimaal 4 (één baan dubbelspel). In de praktijk werkt het pas leuk vanaf 8-12 spelers, zodat er voldoende variatie in koppels is.</div>
    </details></div>
    </div>
  </section>`;

const WatIsHusselen = () => {
  return (
    <div className="min-h-screen flex flex-col bg-background text-foreground">
      <ContentPageNav showPricing />
      <div
        className="flex-1 marketing-content"
        dangerouslySetInnerHTML={{ __html: html }}
      />
      <FaqSchema html={html} />
      <Footer />
    </div>
  );
};

export default WatIsHusselen;
